import type { PlayerId, ZoneId } from "../types/common";
import type { PlayerZones, WarchestState } from "../types/game-state";
import { shuffleBag } from "./rng";

export function hasCoin(
  zones: PlayerZones,
  coinType: string,
  zone: ZoneId,
): boolean {
  return zones[zone].includes(coinType);
}

export function countCoins(
  zones: PlayerZones,
  coinType: string,
  zone: ZoneId,
): number {
  return zones[zone].filter((c) => c === coinType).length;
}

export function removeCoin(
  arr: readonly string[],
  coinType: string,
): readonly string[] {
  const idx = arr.indexOf(coinType);
  if (idx === -1) return arr;
  return [...arr.slice(0, idx), ...arr.slice(idx + 1)];
}

export function moveCoin(
  zones: PlayerZones,
  coinType: string,
  from: ZoneId,
  to: ZoneId,
): PlayerZones {
  const fromArr = removeCoin(zones[from], coinType);
  const toArr = [...zones[to], coinType];
  return { ...zones, [from]: fromArr, [to]: toArr };
}

// Draws up to `count` coins from bag → hand.
// When bag empties mid-draw, shuffles discardFaceUp + discardFaceDown back into bag.
// Bag is treated as pre-shuffled; we always draw from index 0.
export function drawCoinsFromBag(
  zones: PlayerZones,
  count: number,
  rngState: number,
): { zones: PlayerZones; rngState: number } {
  let current = zones;
  let rng = rngState;

  for (let i = 0; i < count; i += 1) {
    if (current.bag.length === 0) {
      const discard = [...current.discardFaceUp, ...current.discardFaceDown];
      if (discard.length === 0) break;
      const { shuffled, nextState } = shuffleBag(discard, rng);
      rng = nextState;
      current = {
        ...current,
        bag: shuffled,
        discardFaceUp: [],
        discardFaceDown: [],
      };
    }
    const coinType = current.bag[0] as string;
    current = {
      ...current,
      bag: current.bag.slice(1),
      hand: [...current.hand, coinType],
    };
  }

  return { zones: current, rngState: rng };
}

export function getOpponent(player: PlayerId): PlayerId {
  return player === "P1" ? "P2" : "P1";
}

export function countControlledLocations(
  locations: Readonly<
    Record<string, { readonly controlledBy: PlayerId | null }>
  >,
  player: PlayerId,
): number {
  return Object.values(locations).filter((l) => l.controlledBy === player)
    .length;
}

export function advanceActivePlayer(
  activePlayer: PlayerId,
  playerZones: Readonly<Record<PlayerId, PlayerZones>>,
): PlayerId {
  const opponent = getOpponent(activePlayer);
  if (playerZones[opponent].hand.length > 0) return opponent;
  return activePlayer;
}

export function allHandsEmpty(
  playerZones: Readonly<Record<PlayerId, PlayerZones>>,
): boolean {
  return playerZones.P1.hand.length === 0 && playerZones.P2.hand.length === 0;
}

// A unit type is "eliminated" for a player when at least one of its coins is
// in `removed` and no coins of that type remain anywhere else in play for
// that player (bag/hand/discards/supply or deployed on the board). Distinct,
// in first-removed order. Pure read-only selector.
//
// A bolstered board cell (stackSize > 1) counts as ONE board presence, not
// `stackSize` copies — the extra bolster coin was moved hand → discardFaceUp,
// so it is already counted by the zone scan. Counting it again per the board
// cell would double-count.
export function getEliminatedUnits(
  state: WarchestState,
  player: PlayerId,
): string[] {
  const z = state.playerZones[player];
  const active = new Map<string, number>();
  const bump = (u: string): void => {
    active.set(u, (active.get(u) ?? 0) + 1);
  };
  for (const u of z.bag) bump(u);
  for (const u of z.hand) bump(u);
  for (const u of z.discardFaceDown) bump(u);
  for (const u of z.discardFaceUp) bump(u);
  for (const u of z.supply) bump(u);
  for (const cell of Object.values(state.board)) {
    if (cell && cell.owner === player) bump(cell.unitType);
  }
  const eliminated: string[] = [];
  const seen = new Set<string>();
  for (const u of z.removed) {
    if (seen.has(u)) continue;
    seen.add(u);
    if ((active.get(u) ?? 0) === 0) eliminated.push(u);
  }
  return eliminated;
}
